import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import './styles.css';

const Delta = Quill.import('delta');

const Editor = forwardRef(
  ({ readOnly, defaultValue, onTextChange, onSelectionChange, onChange, name }, ref) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onChangeRef = useRef(onChange);
    const quillRef = useRef(null);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onChangeRef.current = onChange;
    });

    const getDefaultValueAsDelta = (value) => {
      if (!value) return new Delta();

      if (value.ops) return value;

      if (typeof value === 'string') {
        const tempQuill = new Quill(document.createElement('div'));
        return tempQuill.clipboard.convert({ html: value });
      }

      return new Delta();
    };

    useEffect(() => {
      defaultValueRef.current = defaultValue;
      if (quillRef.current && defaultValue) {
        const delta = getDefaultValueAsDelta(defaultValue);
        quillRef.current.setContents(delta);
      }
    }, [defaultValue]);

    useEffect(() => {
      quillRef.current?.enable(!readOnly);
    }, [readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div'),
      );

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean']
          ]
        }
      });

      quillRef.current = quill;

      if (ref) {
        ref.current = quill;
      }

      if (defaultValueRef.current) {
        const delta = getDefaultValueAsDelta(defaultValueRef.current);
        quill.setContents(delta);
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);

        if (onChangeRef.current) {
          const html = editorContainer.querySelector('.ql-editor').innerHTML;
          onChangeRef.current(html);
        }
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      return () => {
        quillRef.current = null;
        if (ref) ref.current = null;
        container.innerHTML = '';
      };
    }, [ref]);

    return <div className="custom-quill-editor" ref={containerRef}></div>;
  },
);

Editor.displayName = 'Editor';

export default Editor;
